import {createIntl, IntlShape} from "react-intl"
import {
    I18nIndividual as GedcomIndividual,
    IndividualBindings as DeserializedIndividual,
    capitalize
} from "../model/gedcom-individual"
import {FamilyBindings as DeserializedFamily, I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {GedcomLanguage as Language} from "../model/gedcom-language"
import {individualsHeader} from "../model/xlsx-individual-header"
import {familiesHeader} from "../model/xlsx-family-header"
import {saveAs} from "file-saver"
import {DateOrYear} from "../datepicker/date-or-year"
import {getI18nMessages, Locale} from "../locale"
import {validatePipeline} from "../converter/gedcom-validate"
import {ImportError, ValidationErrors} from "../error/converter-errors"
import md5 from "md5"
import {WorkBook, WorkSheet} from "xlsx"
import * as xlsx from "xlsx"
import * as Sentry from "@sentry/react"


const bookProps = {
    Author: "gedcomcreator.com",
    Category: "Genealogy",
    Keywords: "GEDCOM, genealogy, family",
    Comments: "Generated with gedcomcreator.com",
    // CreatedDate: to be filled at the moment of export
}

/**
 * Disable editing.
 */
const protectOptions = {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: true,
    formatColumns: false,
    formatRows: true,
    insertColumns: true,
    insertRows: true,
    deleteColumns: true,
    deleteRows: true,
    sort: true,
    autoFilter: true,
    pivotTables: true,
    objects: true,
    scenarios: true
}

export interface Metadata {
    version: number
    fileLocale: Locale
    editable: boolean
    generatedIndividualsSig?: string
    generatedFamiliesSig?: string
    computedIndividualsSig?: string
    computedFamiliesSig?: string
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* EXPORT                                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */

export async function exportXlsx(
    individualsData: GedcomIndividual[],
    familiesData: GedcomFamily[],
    creatorKey: number,
    intl: IntlShape,
    filename: string,
    editable: boolean
) {
    // Serialize complex fields
    const wb = xlsx.utils.book_new()
    const { serializedIndividuals, indiHeader } = serializeIndividuals(individualsData, intl)
    const { serializedFamilies, famHeader } = serializeFamilies(familiesData, intl)
    const wsIndividuals = xlsx.utils.json_to_sheet(serializedIndividuals, { header:  indiHeader })
    const wsFamilies = xlsx.utils.json_to_sheet(serializedFamilies, { header:  famHeader })
    protect(editable, wb, serializedIndividuals, serializedFamilies, wsIndividuals, wsFamilies, creatorKey, intl)
    xlsx.utils.book_append_sheet(wb, wsIndividuals, intl.formatMessage({id: "creator.tabs.individuals"}))
    xlsx.utils.book_append_sheet(wb, wsFamilies, intl.formatMessage({id: "creator.tabs.families"}))
    wb.Workbook = { Sheets: wb.SheetNames.map((sheet) => ({ sheet, Hidden: sheet === "meta" ? 2 : 0 })) }  // non-unhidable
    // Write workbook and trigger download
    const wbOut = xlsx.write(wb, { Props: { ...bookProps, CreatedDate: new Date() }, bookType: "xlsx", type: "array", bookSST: false })
    const blob = new Blob([wbOut], { type: "application/octet-stream" })
    saveAs(blob, filename)
    console.log(`Exported as '${filename}'`)
    Sentry.captureMessage(`Exported progress '${filename}'`, { level: "log" })
}

function protect(
    editable: boolean,
    wb: WorkBook,
    serializedIndividuals: Record<string, string>[],
    serializedFamilies: Record<string, string>[],
    wsIndividuals: WorkSheet,
    wsFamilies: WorkSheet,
    creatorKey: number,
    intl: IntlShape
) {
    let individualsSig, familiesSig
    // Restrict edition
    if (!editable) {
        individualsSig = md5(JSON.stringify(serializedIndividuals))
        familiesSig = md5(JSON.stringify(serializedFamilies))
        wsIndividuals["!protect"] = {...protectOptions, password: individualsSig}
        wsFamilies["!protect"] = {...protectOptions, password: familiesSig}
    }
    // Add metadata hidden sheet
    xlsx.utils.book_append_sheet(wb, xlsx.utils.aoa_to_sheet([
        ["author", "gedcomcreator.com"],
        ["locale", intl.locale],
        ["version", creatorKey],
        ["editable", editable],
        ...(!editable ? [
            ["individualsSig", individualsSig],
            ["familiesSig", familiesSig]
        ] : [])
    ]), "meta")
    return { individualsSig, familiesSig }
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* IMPORT                                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */

export async function importXlsx(
    file: File,
    allLanguages: Language[],
    intl: IntlShape
): Promise<{ individualsData: GedcomIndividual[], familiesData: GedcomFamily[], metadata: Metadata }> {
    try {
        const fileContent = await file.text()
        // Read file content as text (or arrayBuffer if binary XLSX)
        const arrayBuffer = await file.arrayBuffer() // needed for xlsx
        const data = new Uint8Array(arrayBuffer)
        const workbook = xlsx.read(data, { type: "array" })
        Sentry.captureMessage(`Imported file '${file.name}'.`, { level: "log", extra: { file: fileContent } })
        const metadata = verifyMetadata(file, workbook, intl)
        const fileIntl = createIntl({ locale: metadata.fileLocale || Locale.EN, messages: getI18nMessages(metadata.fileLocale)})
        const wsIndividuals = workbook.Sheets[fileIntl.formatMessage({id: "creator.tabs.individuals"})]
        const wsFamilies = workbook.Sheets[fileIntl.formatMessage({id: "creator.tabs.families"})]
        const individualsData = deserializeIndividuals(wsIndividuals, allLanguages, fileIntl)
        const familiesData = deserializeFamilies(wsFamilies, fileIntl)
        // Validate GEDCOM constraints
        validateImport(file, fileIntl, metadata, individualsData, familiesData, intl)
        console.log(`Imported '${file.name}'`)
        Sentry.captureMessage(`Imported progress '${file.name}'`, { level: "log" })
        return { individualsData, familiesData, metadata }
    } catch (e) {
        throw e
    }
}

function verifyMetadata(file: File, workbook: WorkBook, intl: IntlShape): Metadata {
    const invalidMetadataMessage = intl.formatMessage({ id: "error.import.metadata" })
    if (!workbook.SheetNames.includes("meta")) throw new ImportError(invalidMetadataMessage)
    const metadataSheet = workbook.Sheets["meta"]
    const author = metadataSheet["B1"]?.v
    const fileLocale = metadataSheet["B2"]?.v
    const version = Number(metadataSheet["B3"]?.v)
    const editable = Boolean(metadataSheet["B4"]?.v)
    if (author !== "gedcomcreator.com" || !Object.values(Locale).includes(fileLocale)) throw new ImportError(invalidMetadataMessage)
    verifyLocale(file, fileLocale, intl)
    const { generatedIndividualsSig, generatedFamiliesSig } = parseSignatures(editable, metadataSheet)
    return { version, fileLocale, editable, generatedIndividualsSig, generatedFamiliesSig }
}

function verifyLocale(file: File, fileLocale: String, intl: IntlShape): Locale {
    if (fileLocale !== intl.locale) {
        const localeWarn = `Session language is Locale.${intl.locale.toUpperCase()} but '${file.name}' was exported/imported with Locale.${fileLocale.toUpperCase()}`
        console.log(localeWarn)
        Sentry.captureMessage(localeWarn)
    }
    return fileLocale as Locale
}

function parseSignatures(editable: boolean, metadataSheet: WorkSheet): { generatedIndividualsSig?: string, generatedFamiliesSig?: string } {
    let generatedIndividualsSig: string | undefined
    let generatedFamiliesSig: string | undefined
    if (!editable) {
        generatedIndividualsSig = metadataSheet["B5"]?.v
        generatedFamiliesSig = metadataSheet["B6"]?.v
        console.log(`Imported individuals signature: ${generatedIndividualsSig}`)
        console.log(`Imported families signature: ${generatedFamiliesSig}`)
    }
    return { generatedIndividualsSig, generatedFamiliesSig }
}

function validateImport(file: File, fileIntl: IntlShape, metadata: Metadata, individualsData: GedcomIndividual[], familiesData: GedcomFamily[], intl: IntlShape) {
    // Standard validation for grid records and bindings validation,
    const { validationErrors, validatedIndividuals, validatedFamilies } = validatePipeline(individualsData, familiesData, intl, false)
    // With recomputed bindings, run file tampering validations
    if (!(metadata?.editable)) {
        // Compute hashes for what would look like the serialized originals
        const reSerializedIndividuals = serializeIndividuals(validatedIndividuals, fileIntl).serializedIndividuals
        const reSerializeFamilies =  serializeFamilies(validatedFamilies, fileIntl).serializedFamilies
        metadata!.computedIndividualsSig = md5(JSON.stringify(reSerializedIndividuals))
        metadata!.computedFamiliesSig = md5(JSON.stringify(reSerializeFamilies))
        validateBindings(GedcomIndividual, file, metadata!, validatedIndividuals)
        validateBindings(GedcomFamily, file, metadata!, validatedFamilies)
    }
    if (validationErrors.length > 0)
        throw new ValidationErrors(
            validationErrors,
            intl.formatMessage({ id: "error.import.file"},
            { fileName: file.name, errorsLength: validationErrors.length })
        )
}

/**
 * Deserialized rows from non-import routes will simply skip this since anti-tampering bindings will be absent.
 */
function validateBindings<T extends GedcomIndividual | GedcomFamily>(rowType: typeof GedcomIndividual | typeof GedcomFamily, file: File, metadata: Metadata, rows: T[]) {

    // Validate that the deserialized bindings coincide with freshly calculated ones
    const bindingMismatches = rows.flatMap((row, rowIndex) => Object.entries(row)
        .filter(([col]) => col.startsWith("_"))
        .map(([importBinding, deserializedValue]) => {
            const computedValue = row[importBinding.slice(1)]
            const bindingCoincidence = deserializedValue instanceof Set
                ? deserializedValue.size === (computedValue as Set<any>)?.size && [...deserializedValue].every(v => (computedValue as Set<any>).has(v))
                : deserializedValue === computedValue
            return !bindingCoincidence ? { rowId: row.id, rowIndex, importBinding, deserializedValue, computedValue } : null
        }))
        .filter(Boolean)
        .map(mismatch => `Tampering suspicion for row #${mismatch!.rowIndex} [${mismatch!.rowId}], ` +
            `column '${mismatch!.importBinding.slice(1)}': ` +
            `deserialized=${formatValue(mismatch!.deserializedValue)}, ` +
            `computed=${formatValue(mismatch!.computedValue)}`
        )

    // Validate content signatures
    const signatureMetadata = new Map<typeof GedcomIndividual | typeof GedcomFamily, [generated?: string, computed?: string]>([
        [GedcomIndividual, [metadata.generatedIndividualsSig, metadata.computedIndividualsSig]],
        [GedcomFamily, [metadata.generatedFamiliesSig, metadata.computedFamiliesSig]]
    ])
    const [generated, computed] = signatureMetadata.get(rowType)!
    const signatureMismatches = generated !== computed
        ? [`Tampering suspicion for ${rowType.name.replace("Gedcom", "").toLowerCase()}: generatedSig=${generated}, computedSig=${computed}`]
        : []

    // Send alert report
    if (signatureMismatches.length > 0 || bindingMismatches.length > 0) {
        console.log(`Tampering suspicion for imported file ${file.name}`, { signatureMismatches: signatureMismatches, bindingMismatches: bindingMismatches })
        Sentry.captureMessage(`'Tampering suspicion for imported file ${file.name}'`, {level: "log", extra: {
                "signatureMismatches": signatureMismatches,
                "bindingMismatches": bindingMismatches
            }})
    }
}

const formatValue = (v: any) => {
    if (v instanceof Set) return `[${[...v].map(v => `'${v}'`).join(", ")}]`  // Collection field
    if (typeof v === "string") return `'${v}'`                                                // String
    return v                                                                                  // Number or other primitive
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* SERIALIZATION                                                                                                      */
/* ------------------------------------------------------------------------------------------------------------------ */

function serializeIndividuals(individuals: GedcomIndividual[], intl: IntlShape) {
    const i18nHeader = individualsHeader(intl)
    // Don't include GEDCOM custom tags if not necessary
    const dropEthnicity = individuals.every(i => !i.ethnicity)
    const dropLanguages = individuals.every(i => {
        if (Array.isArray(i.languages)) return i.languages.length === 0
        if (i.languages instanceof Set) return i.languages.size === 0
        return true
    })
    const useHeaderColumns = [...Object.keys(i18nHeader)].filter(col => !(col === "ethnicity" && dropEthnicity) && !(col === "languages" && dropLanguages))
    const serializedRows = individuals.map(({ pristine, restored, ...i }) => {
        const row: Record<string, string> = {}
        useHeaderColumns.forEach(col => {
            row[i18nHeader[col]] = (() => { switch (col) {
                case "birthDate": return i.birthDate ? DateOrYear.serialize(i.birthDate) : ""
                case "deceaseDate": return i.deceaseDate ? DateOrYear.serialize(i.deceaseDate) : ""
                case "familySpouses": return [...i.familySpouses].join(", ")
                case "ethnicity": return dropEthnicity ? "" : (i.ethnicity?.toLowerCase() ?? "")
                case "languages": return dropLanguages ? "" : [...i.languages].map(l => l.name.toLowerCase()).join(", ")
                default: return (i)[col] ?? undefined
            }
            })()})
        return row
    })
    // Remove separator column label
    return { serializedIndividuals: serializedRows, indiHeader: useHeaderColumns.map(col => col === "__SEP__" ? "" : i18nHeader[col]) }
}

function serializeFamilies(families: GedcomFamily[], intl: IntlShape) {
    const i18nHeader = familiesHeader(intl)
    const headerColumns = Object.keys(i18nHeader)
    const serializedRows = families.map(({ pristine, restored, marriaged, divorced, ...f }) => {
        const row: Record<string, string> = {}
        headerColumns.forEach(col => { let value
            switch (col) {
                case "marriageDate": value = f.marriageDate ? DateOrYear.serialize(f.marriageDate) : ""; break
                case "children": value = [...f.children].sort((prev, next) => prev.localeCompare(next)).join(", "); break
                default: value = (f)[col] ?? undefined
            }
            row[i18nHeader[col]] = value
        })
        return row
    })
    // Remove separator column label
    return { serializedFamilies: serializedRows, famHeader: headerColumns.map(col => col === "__SEP__" ? "" : i18nHeader[col]) }
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* DESERIALIZATION                                                                                                    */
/* ------------------------------------------------------------------------------------------------------------------ */

function deserializeIndividuals(individuals: WorkSheet, allLanguages: Language[], intl: IntlShape): GedcomIndividual[] {
    const rows = xlsx.utils.sheet_to_json<any>(individuals)
    const deserializedHeader = deserializeHeader(individualsHeader(intl))
    const result: GedcomIndividual[] = []
    rows.forEach((row, i) => {
        logProgress(GedcomIndividual, rows, i)
        const indi = deserializedHeader(row)
        if ("" in indi) delete indi[""]
        result.push({
            ...indi,
            pristine: true,
            restored: false,
            birthDate: indi.birthDate ? DateOrYear.deserialize(indi.birthDate) : undefined,
            deceaseDate: indi.deceaseDate ? DateOrYear.deserialize(indi.deceaseDate) : undefined,
            ethnicity: capitalize(indi.ethnicity) ?? undefined,
            languages: indi.languages ? new Set(String(indi.languages)
                .split(",")
                .map(s => s.trim())
                .filter(Boolean)
                .map(langName => allLanguages.find(l => l.name.toLowerCase() === langName.toLowerCase()))
                .filter(Boolean) as Language[]) : new Set<Language>(),
            numberOfChildren: 0,
            familyChild: undefined,
            numberOfMarriages: 0,
            familySpouses: new Set<string>(),
            _numberOfChildren: indi.numberOfChildren,
            _familyChild: indi.familyChild,
            _numberOfMarriages: indi.numberOfMarriages,
            _familySpouses: indi.familySpouses
                ? new Set(String(indi.familySpouses).split(",").map((s) => s.trim()).filter(Boolean))
                : new Set<string>()
        } as unknown as DeserializedIndividual)
    })
    return result
}

function deserializeFamilies(families: WorkSheet, intl: IntlShape): GedcomFamily[] {
    const rows = xlsx.utils.sheet_to_json<any>(families)
    const deserializedHeader = deserializeHeader(familiesHeader(intl))
    const result: GedcomFamily[] = []
    rows.forEach((row, i) => {
        logProgress(GedcomFamily, rows, i)
        const fam = deserializedHeader(row)
        if ("" in fam) delete fam[""]
        result.push({
            ...fam,
            pristine: true,
            restored: false,
            marriaged: true,
            marriageDate: fam.marriageDate ? DateOrYear.deserialize(fam.marriageDate) : undefined,
            children: new Set<string>(),
            _children: fam.children
                ? new Set(String(fam.children).split(",").map((s) => s.trim()).filter(Boolean))
                : new Set<string>()
        } as DeserializedFamily)
    })
    return result
}

const importBatch = 100
function logProgress(rowType: typeof GedcomIndividual | typeof GedcomFamily, rows: any[], i: number) {
    if (i % importBatch === 0 || i === rows.length - 1) {
        const percent = ((i + 1) / rows.length * 100).toFixed(1)
        console.log(`Deserializing ${rowType.name}: ${percent}% (${i + 1}/${rows.length})`)
    }
}

function deserializeHeader(header: Record<string, any>) {
    const colFields = Object.keys(header).reduce<Record<string, any>>((fieldMap, field) => {
        if (field === "__SEP__" || header[field] == null) return fieldMap  // skip
        const col = String(header[field]).toLowerCase().replace(/\s+/g, "_")
        fieldMap[col] = field
        return fieldMap
    }, {})
    return (row: Record<string, any>) => Object.keys(row).reduce<Record<string, any>>((deserializedRow, col) => {
        if (col in colFields) deserializedRow[colFields[col]] = row[col]
        return deserializedRow
    }, {})
}